import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, KeyRound, Loader2, Plus, Store as StoreIcon } from 'lucide-react';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, KeyValue, ListPage, ListToolbar,
  Notice, PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from '../shared/listPage';
import { SheetField } from '../settings/sheet';
import { OrgStore, PriceListRef } from './orgTypes';
import { storeLabel } from './orgFormat';
import OrgCredentialsPanel from './OrgCredentialsPanel';
import OrgConfirm from './OrgConfirm';

/**
 * «Καταστήματα» των κεντρικών στη διάταξη «Πίνακας» (14/10/2026): αναζήτηση, πίνακας, συρτάρι με τα
 * στοιχεία του καταστήματος. Ό,τι υπήρχε στη γραμμή του παλιού καταλόγου είναι εδώ, με τις ΙΔΙΕΣ κλήσεις:
 * ονομασία υποκαταστήματος (αποθήκευση όταν φύγεις από το πεδίο), τιμοκατάλογος (αμέσως), κωδικοί,
 * είσοδος στο κατάστημα, αφαίρεση από τον οργανισμό· «Προσθήκη καταστήματος» με αριθμό καταστήματος.
 * Δικαιώματα όπως πριν: όλα εκτός από την «Είσοδο» μόνο για ιδιοκτήτη/διαχειριστή.
 *
 * Το ίδιο και στο πάνελ support → «Αλυσίδες» (25/10/2026): χωρίς «Είσοδο» (χωρίς onSwitchStore) και χωρίς «Κωδικούς»
 * (showCredentials=false — το support έχει την «Πρόσβαση» στην κάρτα καταστήματος)· ο τιμοκατάλογος εκεί ρωτά πρώτα
 * (confirmPriceList), γιατί ξαναβάζει τιμές αμέσως.
 */

export interface OrgStoresTabProps {
  orgId: number;
  stores: OrgStore[];
  priceLists: PriceListRef[];
  canManage: boolean;
  switching: number | null;
  /** Χωρίς αυτό: καμία «Είσοδος» (π.χ. support). */
  onSwitchStore?: (storeId: number) => void;
  onSetStoreLabel: (storeId: number, value: string) => Promise<void> | void;
  onAssignPriceList: (storeId: number, priceListId: string) => Promise<void> | void;
  /** Επιστρέφει μήνυμα σφάλματος (null = πέτυχε). */
  onAddStore: (storeId: string, priceListId: string) => Promise<string | null>;
  onRemoveStore: (storeId: number) => Promise<void> | void;
  /** «Κωδικοί» του καταστήματος (προεπιλογή ναι). */
  showCredentials?: boolean;
  /** Επιβεβαίωση πριν από την αλλαγή τιμοκαταλόγου (αλλάζει τιμές αμέσως). */
  confirmPriceList?: boolean;
}

const OrgStoresTab: React.FC<OrgStoresTabProps> = ({
  orgId, stores, priceLists, canManage, switching, onSwitchStore, onSetStoreLabel, onAssignPriceList, onAddStore, onRemoveStore,
  showCredentials = true, confirmPriceList = false,
}) => {
  const { t } = useTranslation(['orgDashboard', 'admin', 'common', 'listPage']);
  const uid = useId();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [credStore, setCredStore] = useState<OrgStore | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<OrgStore | null>(null);
  const [adding, setAdding] = useState(false);
  const [newStoreId, setNewStoreId] = useState('');
  const [newPriceList, setNewPriceList] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [pendingPl, setPendingPl] = useState<{ store: OrgStore; value: string } | null>(null);

  const selected = useMemo(() => stores.find(s => s.id === selectedId) || null, [stores, selectedId]);
  const rows = useMemo(
    () => stores.filter(s => matchesSearch(search, storeLabel(s), s.name, s.id, s.city)),
    [stores, search],
  );
  const priceListName = (id: number | null | undefined) => (id ? priceLists.find(pl => pl.id === id)?.name : null) || t('orgDashboard:stores.basePriceList');

  const columns = useMemo<Column<OrgStore>[]>(() => [
    {
      key: 'name',
      header: t('orgDashboard:stores.colStore'),
      width: 'minmax(220px, 2fr)',
      render: s => (
        <>
          <span className="block font-medium truncate">{storeLabel(s)}</span>
          <span className="block text-xs text-t3 truncate">
            {s.display_name ? `${s.name} · ` : ''}#{s.id}
          </span>
        </>
      ),
      stackRender: s => storeLabel(s),
      sortable: true,
      sortValue: s => storeLabel(s),
    },
    {
      key: 'city',
      header: t('orgDashboard:stores.colCity'),
      width: 'minmax(120px, 1fr)',
      render: s => (s.city ? <span className="text-t2">{s.city}</span> : <span className="text-t4">—</span>),
      stackRender: s => s.city || null,
      sortable: true,
      sortValue: s => s.city || '',
    },
    ...(priceLists.length > 0 ? [{
      key: 'price_list',
      header: t('orgDashboard:stores.colPriceList'),
      width: 'minmax(140px, 1fr)',
      hideBelow: 'medium',
      render: (s: OrgStore) => <span className="text-t2">{priceListName(s.price_list_id)}</span>,
    } as Column<OrgStore>] : []),
    {
      key: 'status',
      header: t('common:status'),
      width: '120px',
      render: s => (s.is_active
        ? <StatusChip tone="ok">{t('common:active')}</StatusChip>
        : <StatusChip tone="muted">{t('common:inactive')}</StatusChip>),
    },
    ...(onSwitchStore ? [{
      key: 'enter',
      header: <span className="sr-only">{t('admin:enterStoreTitle')}</span>,
      width: '128px',
      align: 'right',
      hideBelow: 'narrow',
      // Η «Είσοδος» μένει ένα πάτημα από τη γραμμή, όπως πριν· το κλικ στη γραμμή ανοίγει τα στοιχεία.
      render: (s: OrgStore) => (
        <button
          type="button"
          className="org-enter"
          onClick={() => onSwitchStore(s.id)}
          disabled={switching === s.id}
          title={t('admin:enterStoreTitle')}
          data-testid={`org-store-enter-${s.id}`}
        >
          {switching === s.id ? <Loader2 className="animate-spin" aria-hidden="true" /> : <ArrowRightLeft aria-hidden="true" />}
          {t('admin:enterStore')}
        </button>
      ),
    } as Column<OrgStore>] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- priceListName διαβάζει priceLists/t
  ], [t, priceLists, switching, onSwitchStore]);

  const closeAdd = () => {
    setAdding(false);
    setNewStoreId('');
    setNewPriceList('');
    setAddError(null);
  };

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStoreId || addBusy) return;
    setAddBusy(true);
    setAddError(null);
    try {
      const err = await onAddStore(newStoreId, newPriceList);
      if (err) setAddError(err);
      else closeAdd();
    } finally {
      setAddBusy(false);
    }
  };

  const confirmRemoval = async () => {
    const target = confirmRemove;
    setConfirmRemove(null);
    if (!target) return;
    await onRemoveStore(target.id);
    setSelectedId(null);
  };

  // Το Esc/φόντο με ανοιχτή επιβεβαίωση κλείνει ΠΡΩΤΑ την επιβεβαίωση, όχι το συρτάρι από κάτω.
  const closeDetail = () => {
    if (pendingPl) setPendingPl(null);
    else if (confirmRemove) setConfirmRemove(null);
    else setSelectedId(null);
  };

  const filtered = search.trim() !== '';

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.org')}
      title={t('orgDashboard:stores.title')}
      subtitle={canManage ? t('orgDashboard:stores.subtitle') : t('orgDashboard:stores.subtitleView')}
      testId="org-stores"
    >
      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('orgDashboard:stores.searchPlaceholder'),
          ariaLabel: t('orgDashboard:stores.searchLabel'),
        }}
        actions={canManage ? (
          <button type="button" className={PRIMARY_BTN} onClick={() => setAdding(true)} data-testid="org-store-add">
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('admin:addStore2')}
          </button>
        ) : null}
        summary={filtered
          ? t('orgDashboard:stores.countFiltered', { shown: rows.length, count: stores.length })
          : t('orgDashboard:stores.count', { count: stores.length })}
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={s => s.id}
        onRowClick={s => setSelectedId(s.id)}
        selectedKey={selectedId}
        stickyHeader
        defaultSort={null}
        rowLabel={s => t('orgDashboard:stores.rowLabel', { name: storeLabel(s), id: s.id })}
        rowClassName={s => (s.is_active ? undefined : 'opacity-70')}
        empty={filtered
          ? <EmptyState icon={StoreIcon} title={t('orgDashboard:stores.noMatch')} hint={t('orgDashboard:stores.noMatchHint')} />
          : <EmptyState icon={StoreIcon} title={t('orgDashboard:stores.empty')} hint={t('admin:noStoresYet2')} />}
        testId="org-stores-table"
      />

      {/* Στοιχεία καταστήματος */}
      <DetailPanel
        open={selected !== null}
        onClose={closeDetail}
        title={selected ? storeLabel(selected) : ''}
        subtitle={selected ? [`#${selected.id}`, selected.city].filter(Boolean).join(' · ') : undefined}
        testId="org-store-detail"
        footerStart={selected && canManage ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirmRemove(selected)} data-testid="org-store-remove">
            {t('admin:removeFromOrg')}
          </button>
        ) : undefined}
        footer={selected && ((showCredentials && canManage) || onSwitchStore) ? (
          <>
            {showCredentials && canManage && (
              <button
                type="button"
                className={GHOST_BTN}
                onClick={() => { setCredStore(selected); setSelectedId(null); }}
                data-testid="org-store-credentials"
              >
                <KeyRound className="w-4 h-4" aria-hidden="true" />
                {t('orgDashboard:stores.credentials')}
              </button>
            )}
            {onSwitchStore && (
              <button
                type="button"
                className={PRIMARY_BTN}
                onClick={() => onSwitchStore(selected.id)}
                disabled={switching === selected.id}
                title={t('admin:enterStoreTitle')}
                data-testid="org-store-detail-enter"
              >
                {switching === selected.id ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ArrowRightLeft className="w-4 h-4" aria-hidden="true" />}
                {t('admin:enterStore')}
              </button>
            )}
          </>
        ) : undefined}
      >
        {selected && (
          <div className="space-y-6">
            {canManage && (
              <SheetField
                label={t('orgDashboard:stores.displayName')}
                htmlFor={`${uid}-label`}
                hint={t('orgDashboard:stores.displayNameHint')}
              >
                <input
                  key={selected.id}
                  id={`${uid}-label`}
                  type="text"
                  defaultValue={selected.display_name || ''}
                  placeholder={selected.city ? t('orgDashboard:stores.displayNamePlaceholderCity', { city: selected.city }) : t('orgDashboard:stores.displayNamePlaceholder')}
                  aria-describedby={`${uid}-label-hint`}
                  onBlur={e => { if ((e.target.value.trim() || null) !== (selected.display_name || null)) onSetStoreLabel(selected.id, e.target.value); }}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  className={INPUT_CLS}
                  data-testid="org-store-label-input"
                />
              </SheetField>
            )}
            {canManage && priceLists.length > 0 && (
              <SheetField label={t('orgDashboard:stores.colPriceList')} htmlFor={`${uid}-pl`} hint={t('orgDashboard:stores.priceListHint')}>
                <select
                  id={`${uid}-pl`}
                  value={selected.price_list_id ?? ''}
                  onChange={e => (confirmPriceList
                    ? setPendingPl({ store: selected, value: e.target.value })
                    : onAssignPriceList(selected.id, e.target.value))}
                  className={SELECT_CLS}
                  data-testid="org-store-pricelist"
                >
                  <option value="">{t('orgDashboard:stores.basePriceList')}</option>
                  {priceLists.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
                </select>
              </SheetField>
            )}
            <KeyValue
              testId="org-store-values"
              items={[
                { id: 'legal', label: t('orgDashboard:stores.legalName'), value: selected.name },
                ...(!canManage ? [{ id: 'display', label: t('orgDashboard:stores.displayName'), value: selected.display_name || null }] : []),
                ...(!canManage && priceLists.length > 0 ? [{ id: 'pl', label: t('orgDashboard:stores.colPriceList'), value: priceListName(selected.price_list_id) }] : []),
                { id: 'city', label: t('orgDashboard:stores.colCity'), value: selected.city || null },
                { id: 'address', label: t('orgDashboard:stores.address'), value: selected.address || null },
                { id: 'phone', label: t('common:phone'), value: selected.phone_number || null },
                { id: 'email', label: t('common:email'), value: selected.email || null },
                {
                  id: 'status',
                  label: t('common:status'),
                  value: selected.is_active
                    ? <StatusChip tone="ok">{t('common:active')}</StatusChip>
                    : <StatusChip tone="muted">{t('common:inactive')}</StatusChip>,
                },
              ]}
            />
          </div>
        )}
      </DetailPanel>

      {/* Νέο κατάστημα στον οργανισμό: με τον αριθμό του (όπως πριν) */}
      <DetailPanel
        open={adding}
        onClose={closeAdd}
        title={t('admin:addStoreTitle')}
        subtitle={t('orgDashboard:stores.addSubtitle')}
        testId="org-store-add-panel"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeAdd}>{t('orgDashboard:actions.cancel')}</button>
            <button type="submit" form={`${uid}-add`} className={PRIMARY_BTN} disabled={!newStoreId || addBusy} data-testid="org-store-add-submit">
              {addBusy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('admin:addButton')}
            </button>
          </>
        )}
      >
        <form id={`${uid}-add`} onSubmit={submitAdd} noValidate className="space-y-5">
          <SheetField label={t('admin:storeIdLabel')} htmlFor={`${uid}-sid`} hint={t('orgDashboard:stores.storeIdHint')}>
            <input
              id={`${uid}-sid`}
              type="number"
              value={newStoreId}
              onChange={e => setNewStoreId(e.target.value)}
              placeholder={t('admin:storeIdPlaceholder')}
              className={INPUT_CLS}
              autoFocus
              data-testid="org-store-add-id"
            />
          </SheetField>
          {priceLists.length > 0 && (
            <SheetField label={t('orgDashboard:stores.colPriceList')} htmlFor={`${uid}-npl`}>
              <select id={`${uid}-npl`} value={newPriceList} onChange={e => setNewPriceList(e.target.value)} className={SELECT_CLS}>
                <option value="">{t('orgDashboard:stores.basePriceListDefault')}</option>
                {priceLists.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
              </select>
            </SheetField>
          )}
          {addError && <Notice tone="danger" testId="org-store-add-error">{addError}</Notice>}
        </form>
      </DetailPanel>

      {showCredentials && <OrgCredentialsPanel orgId={orgId} store={credStore} onClose={() => setCredStore(null)} />}

      <OrgConfirm
        isOpen={pendingPl !== null}
        title={t('orgDashboard:stores.priceListConfirmTitle')}
        message={pendingPl ? t('orgDashboard:stores.priceListConfirm', {
          name: storeLabel(pendingPl.store),
          list: pendingPl.value ? (priceLists.find(pl => String(pl.id) === pendingPl.value)?.name || pendingPl.value) : t('orgDashboard:stores.basePriceList'),
        }) : ''}
        confirmLabel={t('orgDashboard:stores.priceListConfirmButton')}
        cancelLabel={t('orgDashboard:actions.cancel')}
        variant="warning"
        onConfirm={() => { const p = pendingPl; setPendingPl(null); if (p) onAssignPriceList(p.store.id, p.value); }}
        onCancel={() => setPendingPl(null)}
      />

      <OrgConfirm
        isOpen={confirmRemove !== null}
        title={t('admin:removeFromOrg')}
        message={confirmRemove ? `${storeLabel(confirmRemove)} — ${t('admin:confirmRemoveStore')}` : ''}
        confirmLabel={t('orgDashboard:stores.removeConfirm')}
        cancelLabel={t('orgDashboard:actions.cancel')}
        variant="danger"
        onConfirm={confirmRemoval}
        onCancel={() => setConfirmRemove(null)}
      />
    </ListPage>
  );
};

export default OrgStoresTab;
